import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, TrendingUp, FileText, Settings, Car, LogOut, BarChart2, ChevronDown, Briefcase
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ScreenSaver } from "@/components/ScreenSaver";

const dropdownMenus = {
  vidskipti: [
    { path: "/vidskiptamenn", label: "Viðskiptamenn", icon: Users },
    { path: "/reikningar", label: "Reikningar", icon: FileText },
    { path: "/applications", label: "Umsóknir", icon: FileText },
    { path: "/afsol", label: "Afsöl", icon: Car },
  ],
  skyrslu: [
    { path: "/skyrslu?section=utistandandi", label: "Útistandandi Skuld" },
    { path: "/skyrslu?section=nastu12", label: "Næstu 12 Mánuðir" },
    { path: "/skyrslu?section=uppgjor", label: "Uppgjör" },
    { path: "/skyrslu?section=agent", label: "Agent" },
  ],
};

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [openMenu, setOpenMenu] = useState(null);
  const [screenSaver, setScreenSaver] = useState(false);
  const closeTimer = useRef(null);

  const handleLogout = () => {
    logout();
    toast.success('Þú hefur verið skráður út');
    navigate('/login');
  };

  const openDropdown = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  const closeDropdown = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const navItems = [
    { path: "/", label: "Yfirlit", icon: TrendingUp, testId: "nav-yfirlit" },
    { key: "vidskipti", label: "Viðskipti", icon: Briefcase, testId: "nav-vidskipti", dropdown: "vidskipti",
      activePaths: ["/vidskiptamenn", "/reikningar", "/applications", "/afsol"] },
    { key: "skyrslu", label: "Skýrslur", icon: BarChart2, testId: "nav-skyrslur", dropdown: "skyrslu",
      activePaths: ["/skyrslu"] },
    { path: "/settings", label: "Stillingar", icon: Settings, testId: "nav-stillingar" }
  ];

  return (
    <>
    {screenSaver && <ScreenSaver onExit={() => { setScreenSaver(false); navigate("/"); }} />}
    <nav className="border-b" style={{ background: "#0f1a2e" }} data-testid="main-navigation">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between h-14 px-5">
        {/* Brand */}
        <button onClick={() => setScreenSaver(true)} className="flex items-center gap-2.5 group shrink-0" data-testid="nav-brand">
          <div className="flex flex-col">
            <span className="text-[17px] font-medium tracking-[0.2em] text-white leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              FONDO
            </span>
            <span className="text-[8px] tracking-[0.15em] leading-none" style={{ color: "#5a6a82" }}>— EST. 2016 —</span>
          </div>
        </button>

        {/* Nav items - centered */}
        <div className="flex items-center gap-1 justify-end flex-1 mx-4">
            {navItems.map(item => {
              const Icon = item.icon;

              if (item.dropdown) {
                const menuItems = dropdownMenus[item.dropdown];
                const isActive = (item.activePaths || []).some(p =>
                  location.pathname === p || location.pathname.startsWith(p + "/")
                ) || (item.dropdown === "skyrslu" && location.pathname === "/skyrslu");
                const isOpen = openMenu === item.dropdown;

                return (
                  <div
                    key={item.key}
                    className="relative"
                    onMouseEnter={() => openDropdown(item.dropdown)}
                    onMouseLeave={closeDropdown}
                  >
                    <button
                      onClick={() => {
                        const first = menuItems[0];
                        navigate(first.path);
                        setOpenMenu(null);
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150"
                      style={{
                        background: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                        color: isActive ? "#fff" : "#94a3b8",
                        boxShadow: isActive ? "0 0 0 1px rgba(255,255,255,0.15)" : "none",
                      }}
                      data-testid={item.testId}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                      <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isOpen && (
                      <div
                        className="absolute top-full left-0 mt-1 w-52 rounded-lg shadow-xl border border-gray-700 overflow-hidden z-50"
                        style={{ background: "#1a2742" }}
                      >
                        {menuItems.map(sub => {
                          const SubIcon = sub.icon;
                          const subActive = location.pathname === sub.path || location.pathname + location.search === sub.path;
                          return (
                            <button
                              key={sub.path}
                              onClick={() => {
                                navigate(sub.path);
                                setOpenMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
                              style={{ color: subActive ? "#fff" : "#cbd5e1", background: subActive ? "rgba(255,255,255,0.08)" : "transparent" }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = subActive ? "rgba(255,255,255,0.08)" : "transparent"; e.currentTarget.style.color = subActive ? "#fff" : "#cbd5e1"; }}
                              data-testid={`nav-${item.dropdown}-${sub.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              {SubIcon && <SubIcon size={14} />}
                              {sub.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = item.path === "/"
                ? location.pathname === "/"
                : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-150"
                  style={{
                    background: isActive ? "rgba(255,255,255,0.1)" : "transparent",
                    color: isActive ? "#fff" : "#94a3b8",
                    boxShadow: isActive ? "0 0 0 1px rgba(255,255,255,0.15)" : "none",
                  }}
                  onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.color = "#e2e8f0"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}}
                  onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.background = "transparent"; }}}
                  data-testid={item.testId}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

        {/* Right section */}
        <div className="flex items-center gap-4">
          {user && (
            <div className="hidden text-right md:block">
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "#64748b" }}>Aðgangur</div>
              <div className="text-sm font-medium" style={{ color: "#e2e8f0" }}>{user.full_name}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: "#64748b" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#e2e8f0"; e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#64748b"; e.currentTarget.style.background = "transparent"; }}
            title="Útskrá"
            data-testid="logout-button"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </nav>
    </>
  );
};
